import type { TruckClassInput } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import { useToast } from '../../lib/use-toast';
import { createTruckClass, deactivateTruckClass, listTruckClasses, reactivateTruckClass, updateTruckClass, type TruckClass } from './reference-data.api';

export type TruckClassesState = { status: 'loading' } | { status: 'loaded'; classes: TruckClass[] } | { status: 'forbidden' } | { status: 'error' };

function toErrorState(err: unknown): TruckClassesState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/**
 * The truck-classes catalogue plus its mutating calls. `create`/`update` keep
 * their error inline (a form); `deactivate`/`reactivate` are row actions with
 * no form of their own, so their failures toast instead (CLAUDE.md → Web
 * conventions) — same split as useUsers/useUserList.
 */
export function useTruckClasses() {
  const [state, setState] = useState<TruckClassesState>({ status: 'loading' });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;

    listTruckClasses()
      .then((classes) => {
        if (!cancelled) setState({ status: 'loaded', classes });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /** Re-fetches after a mutation commits — a plain async function, not tied to the effect above. */
  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', classes: await listTruckClasses() });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  async function save(action: () => Promise<unknown>, successMessage: string): Promise<boolean> {
    setSaveError(null);
    setSaving(true);
    try {
      await action();
      await reload();
      toast.success(successMessage);
      return true;
    } catch (err) {
      setSaveError(
        err instanceof ApiError && err.status === 409 ? 'A truck class with this code already exists.' : 'Could not save — check the fields and try again.',
      );
      return false;
    } finally {
      setSaving(false);
    }
  }

  const create = (input: TruckClassInput) => save(() => createTruckClass(input), `${input.name} created`);
  const update = (id: string, input: TruckClassInput) => save(() => updateTruckClass(id, input), `${input.name} updated`);
  const deactivate = (row: TruckClass) => rowAction(() => deactivateTruckClass(row.id), `${row.name} deactivated`, `Couldn't deactivate ${row.name}`);
  const reactivate = (row: TruckClass) => rowAction(() => reactivateTruckClass(row.id), `${row.name} reactivated`, `Couldn't reactivate ${row.name}`);

  return { state, saveError, saving, create, update, resetSaveError: () => setSaveError(null), deactivate, reactivate };
}
